/**
 * /well-testing/invoices — the WTI- invoice register
 * (GET /well-testing/invoices, fieldiq-api#1254, WT-5a). Pipeline &
 * Facility's register, carried over.
 *
 * An invoice is generated from customer-approved field tickets on "New
 * invoice" (the band's action). The status and customer filters live in the
 * URL, so a record's back arrow returns to the list as it was left. Chip counts
 * are tallies of the loaded, unpaginated list (the API returns no count).
 * Every total is the API's, in the invoice's own currency.
 */
import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ClipboardCheck, FileClock, Send } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWtCustomers, useWtMeta } from '../../../hooks/useWellTesting';
import { useWtBillable, useWtInvoices } from '../../../hooks/useWtInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wtDay } from '../../../lib/well-testing';
import { canWriteWtInvoices, wtInvoiceStatusSkin, WT_INVOICE_STATUS_SKIN } from '../../../lib/wt-invoices';
import { useAuth } from '../../../contexts/AuthContext';
import type { WtInvoiceCatalog, WtInvoiceRow } from '../../../types/wt-invoices';
import { useOpenRecord } from '../../downhole/recordPage';
import { WT_EYEBROW, WT_INPUT, WtBadge, WtChip, WtChipRow, WtEmpty, WtPanel, WtRegister, type WtColumn } from '../WtBits';

export const WT_INVOICES_PATH = '/well-testing/invoices';
export const WT_NEW_INVOICE_PATH = '/well-testing/invoices/new';

/** A job column for a row: its job, or "Several jobs" when the invoice bills tickets from more than one. */
export const wtInvoiceJobText = (r: Pick<WtInvoiceRow, 'jobId' | 'jobNumber'>) => r.jobNumber || (r.jobId ? '—' : 'Several jobs');

export const wtInvoiceColumns: WtColumn<WtInvoiceRow>[] = [
  { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'customer', header: 'Customer', render: (r) => <span className="font-semibold text-white">{r.customerName || '—'}</span> },
  { key: 'job', header: 'Job', render: (r) => <span className="font-mono">{wtInvoiceJobText(r)}</span> },
  { key: 'status', header: 'Status', render: (r) => <WtBadge skin={wtInvoiceStatusSkin(r.status)} value={r.status} /> },
  { key: 'date', header: 'Invoice date', render: (r) => wtDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => wtDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono" data-testid="wt-invoice-row-total">{fmtCurrency(r.total, r.currency)}</span> },
];

export default function WtInvoicesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const customer = params.get('customer') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(params);
    if (next) p.set(key, next);
    else p.delete(key);
    setParams(p, { replace: true });
  };
  const catalog = (useWtMeta().data?.data ?? null) as WtInvoiceCatalog | null;
  const customers = useWtCustomers().data ?? [];
  const listQuery = useWtInvoices({ status: status || undefined, customer_id: customer || undefined });
  const allQuery = useWtInvoices();
  const billable = useWtBillable();
  const rows = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: WtInvoiceRow) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const billableKnown = !billable.isLoading && !queryLoadFailure(billable);
  const readyCount = billableKnown ? (billable.data?.data ?? []).reduce((n, g) => n + g.tickets.length, 0) : undefined;
  const statuses = catalog?.invoiceStatuses?.length
    ? catalog.invoiceStatuses
    : Object.entries(WT_INVOICE_STATUS_SKIN).map(([value, s]) => ({ value, label: s.label }));

  return (
    <div data-testid="wt-invoices-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="Invoices"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWriteWtInvoices(user?.role) ? (
            <Link to={WT_NEW_INVOICE_PATH} className="fi-action fi-action--standard fi-action--primary" data-testid="wt-invoices-new-link">
              New invoice
            </Link>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: 'Ready', sub: 'Approved tickets', tone: 'accent', loading: billable.isLoading, value: displayValue(readyCount) },
          { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Not yet sent', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => r.status === 'draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'Sent', sub: 'With the customer', tone: 'info', loading: allQuery.isLoading, value: countOf((r) => r.status === 'sent' || r.status === 'approved') },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Paid', tone: 'success', loading: allQuery.isLoading, value: countOf((r) => r.status === 'paid') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            An invoice bills field tickets the customer has approved — one customer, one currency, one province or state. Make one from{' '}
            <Link className="underline" to={WT_NEW_INVOICE_PATH}>New invoice</Link>.
          </p>
          <WtChipRow label="Filter by status">
            <WtChip label="All" active={!status} onClick={() => setParam('status', '')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {statuses.map((s) => (
              <WtChip key={s.value} label={s.label} active={status === s.value} onClick={() => setParam('status', s.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.status === s.value)} />
            ))}
          </WtChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WT_INPUT} aria-label="Filter by customer" value={customer} onChange={(e) => setParam('customer', e.target.value)} data-testid="wt-invoices-customer">
              <option value="">Every customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WtPanel
            eyebrow="Billed from field tickets"
            title="Invoices"
            testId="wt-invoice-register"
            footer={!listQuery.isLoading && !queryLoadFailure(listQuery) && rows.length ? `${rows.length} invoice${rows.length === 1 ? '' : 's'}` : undefined}
          >
            <WtRegister
              query={listQuery}
              rows={rows}
              columns={wtInvoiceColumns}
              rail={(r) => wtInvoiceStatusSkin(r.status).rail}
              onOpen={(r) => openRecord(`${WT_INVOICES_PATH}/${r.id}`)}
              subject="invoices"
              testId="wt-invoice"
              minWidth="min-w-[960px]"
              empty={status || customer ? <WtEmpty>No invoice matches these filters.</WtEmpty> : <WtEmpty>No invoices yet. Make one from New invoice.</WtEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
                      <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
                        {wtInvoiceJobText(r)} · {r.customerName || '—'}
                      </p>
                    </div>
                    <WtBadge skin={wtInvoiceStatusSkin(r.status)} value={r.status} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                    <span>Dated {wtDay(r.invoiceDate)}</span>
                    <span>Due {wtDay(r.dueDate)}</span>
                    <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span>
                  </div>
                </>
              )}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
